import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { applyEnrichmentAnchors } from "./domAnchors.js";
import { computeDesktopCardPositions } from "./layout.js";

function FootnoteIcon() {
  return (
    <svg className="book-enrichment-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 15.5c-1.5-1.1-2.5-2.9-2.5-5a5.5 5.5 0 1 1 11 0c0 2.1-1 3.9-2.5 5" />
      <path d="M9.5 17.5h5" />
      <path d="M10.2 19.5h3.6" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg className="book-enrichment-icon" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4.5" y="5.5" width="15" height="13" rx="0.8" />
      <path d="M7.5 15l3.2-3.2 2.5 2.3 2.3-2.8 1.9 3.7" />
      <circle cx="10" cy="9.2" r="1" />
    </svg>
  );
}

function SceneIcon() {
  return (
    <svg className="book-enrichment-icon" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4.5" y="5.5" width="15" height="13" rx="0.8" />
      <path d="M10 9l5 3-5 3z" />
    </svg>
  );
}

function ToggleIcon() {
  return (
    <svg className="book-enrichment-toggle-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5.5 6.5h13a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H10l-4.5 3v-3H5.5A1.5 1.5 0 0 1 4 16V8A1.5 1.5 0 0 1 5.5 6.5z" />
      <path d="M8.25 10h7.5" />
      <path d="M8.25 13h5.25" />
    </svg>
  );
}

const KIND_META = {
  footnote: {
    label: "Footnote",
    icon: <FootnoteIcon />,
  },
  image: {
    label: "Image",
    icon: <ImageIcon />,
  },
  scene: {
    label: "Scene",
    icon: <SceneIcon />,
  },
};

function readViewportState() {
  const topbar = document.querySelector(".topbar");
  return {
    scrollY: window.scrollY,
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    topbarHeight: topbar ? topbar.getBoundingClientRect().height : 0,
  };
}

function useViewportState() {
  const [viewport, setViewport] = useState(readViewportState);

  useEffect(function () {
    let frame = 0;

    function updateViewport() {
      frame = 0;
      setViewport(readViewportState());
    }

    function scheduleViewportUpdate() {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(updateViewport);
    }

    window.addEventListener("scroll", scheduleViewportUpdate, { passive: true });
    window.addEventListener("resize", scheduleViewportUpdate);

    return function cleanup() {
      window.removeEventListener("scroll", scheduleViewportUpdate);
      window.removeEventListener("resize", scheduleViewportUpdate);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return viewport;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(function () {
    return window.matchMedia("(max-width: 64rem)").matches;
  });

  useEffect(function () {
    const media = window.matchMedia("(max-width: 64rem)");

    function handleChange(event) {
      setIsMobile(event.matches);
    }

    setIsMobile(media.matches);
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", handleChange);
      return function cleanup() {
        media.removeEventListener("change", handleChange);
      };
    }

    media.addListener(handleChange);
    return function cleanup() {
      media.removeListener(handleChange);
    };
  }, []);

  return isMobile;
}

function useAnchoredItems(book, dataItems) {
  const [items, setItems] = useState([]);

  useEffect(
    function () {
      if (!book || !Array.isArray(dataItems) || !dataItems.length) {
        setItems([]);
        return;
      }

      const result = applyEnrichmentAnchors(book, dataItems);
      setItems(result.items);

      return function cleanup() {
        result.cleanup();
      };
    },
    [book, dataItems]
  );

  return items;
}

function renderPreview(item) {
  const media = item.asset.media;

  if (media && media.kind === "image" && media.src) {
    return (
      <figure className="book-enrichment-preview book-enrichment-preview--media">
        <img src={media.src} alt={media.alt || ""} />
      </figure>
    );
  }

  if (media && media.kind === "video") {
    return (
      <figure className="book-enrichment-preview book-enrichment-preview--scene">
        {media.poster ? (
          <img src={media.poster} alt={media.alt || ""} />
        ) : (
          <div className="book-enrichment-preview-placeholder" />
        )}
        <span className="book-enrichment-preview-badge">Scene</span>
      </figure>
    );
  }

  return <div className="book-enrichment-preview book-enrichment-preview--placeholder" />;
}

function renderFootnoteContent(item) {
  if (item.asset.bodyHtml) {
    return (
      <div
        className="book-enrichment-footnote-content"
        dangerouslySetInnerHTML={{ __html: item.asset.bodyHtml }}
      />
    );
  }

  return <div className="book-enrichment-footnote-content">{item.asset.body}</div>;
}

function EnrichmentCard({ item, index, isActive, styleTop, setRef, onActivate }) {
  const meta = KIND_META[item.asset.kind] || KIND_META.footnote;
  const isImageCard = item.asset.kind === "image";
  const isFootnoteCard = item.asset.kind === "footnote";

  return (
    <article
      ref={setRef}
      className={`book-enrichment-card${isActive ? " is-active" : ""}${
        isImageCard ? " book-enrichment-card--image" : ""
      }${isFootnoteCard ? " book-enrichment-card--footnote" : ""}${
        isFootnoteCard ? " book-enrichment-card--simple" : ""
      }`}
      data-enrichment-card={item.id}
      tabIndex={0}
      style={styleTop == null ? undefined : { top: `${styleTop}px` }}
      onClick={onActivate}
      onKeyDown={function (event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onActivate();
        }
      }}
    >
      {isImageCard ? (
        <>
          {renderPreview(item)}
          <p className="book-enrichment-image-caption">{item.asset.title}</p>
        </>
      ) : isFootnoteCard ? (
        renderFootnoteContent(item)
      ) : (
        <>
          <p className="book-enrichment-kicker">Enrichment {index + 1}</p>
          <p className="book-enrichment-type">
            <span className="book-enrichment-type-icon">{meta.icon}</span>
            {meta.label}
          </p>
          <p className="book-enrichment-quote">{item.quote}</p>
          {renderPreview(item)}
          {item.asset.kind === "footnote" ? null : (
            <>
              <h2 className="book-enrichment-title">{item.asset.title}</h2>
              <p className="book-enrichment-body">{item.asset.body}</p>
              <p className="book-enrichment-location">
                Page {item.asset.pageId.replace("page-", "").toUpperCase()}
              </p>
            </>
          )}
        </>
      )}
    </article>
  );
}

function EnrichmentList({
  items,
  allItems,
  activeId,
  positionsById,
  listRef,
  cardRefs,
  onActivateItem,
}) {
  const itemIndices = useMemo(
    function () {
      return new Map(
        allItems.map(function (item, index) {
          return [item.id, index];
        })
      );
    },
    [allItems]
  );

  return (
    <section ref={listRef} className="book-enrichment-list">
      {items.map(function (item) {
        return (
          <EnrichmentCard
            key={item.id}
            item={item}
            index={itemIndices.get(item.id) || 0}
            isActive={item.id === activeId}
            styleTop={positionsById[item.id]}
            setRef={
              cardRefs
                ? function (node) {
                    if (node) {
                      cardRefs.current.set(item.id, node);
                    } else {
                      cardRefs.current.delete(item.id);
                    }
                  }
                : undefined
            }
            onActivate={function () {
              onActivateItem(item.id, {
                scrollText: true,
              });
            }}
          />
        );
      })}
    </section>
  );
}

function DesktopRail({ items, activeId, viewport, onActivateItem, onHide }) {
  const railListRef = useRef(null);
  const cardRefs = useRef(new Map());
  const [positionsById, setPositionsById] = useState({});
  const renderedItems = items;

  useLayoutEffect(
    function () {
      const listElement = railListRef.current;
      if (!listElement || !renderedItems.length) {
        setPositionsById({});
        return;
      }

      let frame = 0;

      function measureLayout() {
        frame = 0;

        const nextListElement = railListRef.current;
        if (!nextListElement) {
          return;
        }

        const measurementsById = new Map();
        renderedItems.forEach(function (item) {
          const card = cardRefs.current.get(item.id);
          if (!card) {
            return;
          }

          measurementsById.set(item.id, {
            height: Math.max(card.getBoundingClientRect().height, card.offsetHeight, card.scrollHeight),
          });
        });

        setPositionsById(
          computeDesktopCardPositions({
            items: renderedItems,
            listRectTop: nextListElement.getBoundingClientRect().top,
            viewport,
            measurementsById,
          })
        );
      }

      function scheduleMeasure() {
        if (frame) {
          return;
        }

        frame = window.requestAnimationFrame(measureLayout);
      }

      scheduleMeasure();
      window.addEventListener("resize", scheduleMeasure);

      let resizeObserver = null;
      if (typeof ResizeObserver === "function") {
        resizeObserver = new ResizeObserver(scheduleMeasure);
        resizeObserver.observe(listElement);
        renderedItems.forEach(function (item) {
          const card = cardRefs.current.get(item.id);
          if (card) {
            resizeObserver.observe(card);
          }
        });
      }

      return function cleanup() {
        window.removeEventListener("resize", scheduleMeasure);
        if (frame) {
          window.cancelAnimationFrame(frame);
        }
        resizeObserver?.disconnect();
      };
    },
    [activeId, renderedItems, viewport]
  );

  if (!items.length) {
    return null;
  }

  return (
    <aside className="book-enrichment-surface book-enrichment-desktop-rail">
      <div className="book-enrichment-surface-body">
        <header className="book-enrichment-head">
          <p className="wordmark-kicker book-enrichment-head-kicker">Enrichments</p>
          <div className="book-enrichment-head-actions">
            <button
              type="button"
              className="book-enrichment-toggle is-active"
              aria-pressed="true"
              aria-label="Hide enrichments"
              title="Hide enrichments"
              onClick={onHide}
            >
              <ToggleIcon />
            </button>
          </div>
        </header>
        <EnrichmentList
          items={renderedItems}
          allItems={items}
          activeId={activeId}
          positionsById={positionsById}
          listRef={railListRef}
          cardRefs={cardRefs}
          onActivateItem={onActivateItem}
        />
      </div>
    </aside>
  );
}

function EnrichmentLauncher({ onClick }) {
  return (
    <button
      type="button"
      className="book-enrichment-toggle book-enrichment-desktop-launcher"
      aria-label="Show enrichments"
      title="Show enrichments"
      onClick={onClick}
    >
      <ToggleIcon />
    </button>
  );
}

export function ReaderApp({ book, layoutElement, data }) {
  const isMobile = useIsMobile();
  const viewport = useViewportState();
  const items = useAnchoredItems(book, data.items || []);
  const [activeId, setActiveId] = useState(null);
  const [desktopOpen, setDesktopOpen] = useState(function () {
    return !window.matchMedia("(max-width: 64rem)").matches;
  });
  const isEnrichmentVisible = !isMobile && desktopOpen;

  useEffect(
    function () {
      if (!items.length || activeId == null) {
        return;
      }

      const hasActiveItem = items.some(function (item) {
        return item.id === activeId;
      });

      if (!hasActiveItem) {
        setActiveId(null);
      }
    },
    [activeId, items]
  );

  useEffect(
    function () {
      if (!layoutElement) {
        return;
      }

      const showDesktopRail = !isMobile && desktopOpen && items.length > 0;
      layoutElement.classList.toggle("has-desktop-rail", showDesktopRail);

      return function cleanup() {
        layoutElement.classList.remove("has-desktop-rail");
      };
    },
    [desktopOpen, isMobile, items.length, layoutElement]
  );

  useEffect(
    function () {
      items.forEach(function (item) {
        const isActive = isEnrichmentVisible && item.id === activeId;
        item.anchor.tabIndex = isMobile ? -1 : 0;
        item.anchor.classList.toggle("is-active", isActive);
        item.anchor.classList.toggle("is-dimmed", isEnrichmentVisible && item.id !== activeId);

        if (isMobile) {
          item.anchor.removeAttribute("role");
          item.anchor.removeAttribute("aria-expanded");
          return;
        }

        item.anchor.setAttribute("role", "button");
        item.anchor.setAttribute("aria-expanded", isActive ? "true" : "false");
      });
    },
    [activeId, isEnrichmentVisible, isMobile, items]
  );

  useEffect(
    function () {
      if (!book || !items.length || isMobile) {
        return;
      }

      function activateFromAnchor(anchorId) {
        setActiveId(anchorId);
        setDesktopOpen(true);
      }

      function handleClick(event) {
        const anchor = event.target.closest("[data-enrichment-id]");
        if (!anchor) {
          return;
        }

        activateFromAnchor(anchor.dataset.enrichmentId);
      }

      function handleKeyDown(event) {
        const anchor = event.target.closest("[data-enrichment-id]");
        if (!anchor) {
          return;
        }

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activateFromAnchor(anchor.dataset.enrichmentId);
        }
      }

      book.addEventListener("click", handleClick);
      book.addEventListener("keydown", handleKeyDown);

      return function cleanup() {
        book.removeEventListener("click", handleClick);
        book.removeEventListener("keydown", handleKeyDown);
      };
    },
    [book, isMobile, items.length]
  );

  function scrollTextToItem(item) {
    if (!item?.anchor) {
      return;
    }

    window.scrollTo({
      top: Math.max(0, window.scrollY + item.anchor.getBoundingClientRect().top - viewport.topbarHeight - 24),
      behavior: "smooth",
    });
  }

  function activateItem(id, options = {}) {
    const item = items.find(function (entry) {
      return entry.id === id;
    });

    setActiveId(id);

    if (!isMobile) {
      setDesktopOpen(true);
    }

    if (options.scrollText) {
      scrollTextToItem(item);
    }
  }

  const showDesktopRail = !isMobile && desktopOpen && items.length > 0;
  const showLauncher = !isMobile && items.length > 0 && !showDesktopRail;

  return (
    <>
      {showDesktopRail ? (
        <DesktopRail
          items={items}
          activeId={activeId}
          viewport={viewport}
          onActivateItem={activateItem}
          onHide={function () {
            setDesktopOpen(false);
          }}
        />
      ) : null}
      {showLauncher ? (
        <EnrichmentLauncher
          onClick={function () {
            setDesktopOpen(true);
          }}
        />
      ) : null}
    </>
  );
}
